import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { Theme, makeStyles } from '@material-ui/core'
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table'
import {
    FontSettings,
    GlobalStyles,
    ProjectStyle,
    UnitOfMeasureStyles,
} from 'api/graphQL/reactiveVars/styleGuides'
import { TableCellTextContent } from 'api/graphQL/reactiveVars/workspaceContent'
import clsx from 'clsx'
import { ProjectSettings, UnitsOfMeasure } from 'models/projectSettings'
import { indent } from 'pages/PreviewPage/PreviewContent'
import React from 'react'

interface StyleProps {
    globalStyles?: GlobalStyles
    unitOfMeasureStyles?: UnitOfMeasureStyles
}
const getStyles = (styleInput?: FontSettings) => {
    return {
        color: styleInput?.fontColor,
        fontFamily: styleInput?.fontFamily,
        fontSize: `${styleInput?.fontSize}pt`,
        fontStyle: styleInput?.fontStyles?.includes('ITALIC') && 'italic',
        fontWeight: styleInput?.fontStyles?.includes('BOLD') && 'bold',
        textDecoration:
            styleInput?.fontStyles?.includes('UNDERLINE') && 'underline',
    } as object
}

const useStyles = makeStyles((theme: Theme) => ({
    mainTable: {
        border: 'none',
        borderSpacing: 0,
        borderCollapse: 'collapse',
    },
    tableheader: {
        borderBottom: '1px solid lightgray',
        borderRight: '1px solid lightgray',
        padding: theme.spacing(0, 0.5),
        lineHeight: 1.2,
        fontWeight: 'inherit',
        '&:last-child': {
            borderRight: 'none',
        },
    },

    tableCell: {
        borderBottom: '1px solid lightgray',
        borderRight: '1px solid lightgray',
        padding: theme.spacing(0, 0.5),
        whiteSpace: 'pre-wrap',
        '&:last-child': {
            borderRight: 'none',
        },
    },

    tableFooter: {
        color: 'gray',
        '& th': {
            fontWeight: 'normal',
        },
    },
    PRspace: { marginLeft: theme.spacing(2.25) },
    row: {
        display: 'table-row',
    },

    cell: {
        display: 'table-cell',
    },
    PR1: {
        marginLeft: theme.spacing(3),
    },
    PR2: {
        marginLeft: theme.spacing(5),
    },
    PR3: {
        marginLeft: theme.spacing(10),
    },
    PR4: {
        marginLeft: theme.spacing(13.5),
    },
    PR5: {
        marginLeft: theme.spacing(17),
    },
    inputField: {
        outline: 'none',
        border: 'none',
    },
    IP: ({ unitOfMeasureStyles }: StyleProps) => ({
        ...getStyles(unitOfMeasureStyles?.imperial),
    }),
    SI: ({ unitOfMeasureStyles }: StyleProps) => ({
        ...getStyles(unitOfMeasureStyles?.metric),
    }),
}))

interface TableCellFormatBorderColor {
    r: Number
    g: Number
    b: Number
    a: Number
}
interface TableCellFormatBorder {
    color: TableCellFormatBorderColor
    distanceFromText: Number
    lineStyle: 'DOUBLE' | 'SINGLE' | 'NONE' | 'THICK'
    lineWidth: Number
    shadow: boolean
}

interface TableCellFormat {
    bottomBorder: TableCellFormatBorder
    leftBorder: TableCellFormatBorder
    rightBorder: TableCellFormatBorder
    topBorder: TableCellFormatBorder
}
interface TableContent {
    id: string
    alignment: string
    cellFormat: TableCellFormat
    cellPoints: number
    columnNumber: number
    rowNumber: number
    content: string
    columnNumberStart: number
    columnNumberEnd: number
    isMergedCell?: boolean
    textContentElements: Partial<TextContentElement>[]
}

interface TableContentWithChildren extends TableContent {
    children: TableContentWithChildren[]
}

export interface TableElement {
    content: TableContent[]
    elements: TableContent[]
    tableHeader: TableContent[]
    tableIdentifier: string
    totalColumns: number
    totalRows: number
}

export interface TableEditorProps {
    element: TableElement
    isEditable?: boolean
    hierarchicalLevel?: number
    parentCount?: number
    projectSettings?: Partial<ProjectSettings> | undefined
    overrideStyles?: Partial<ProjectStyle>
}

interface AccumulatorObj {
    headerObj: Record<string, TableContent[]>
    value: Record<string, TableContent>[]
    tempObj: Record<string, TableContent>
}

export const TableEditor: React.FC<TableEditorProps> = ({
    element,
    isEditable,
    hierarchicalLevel,
    parentCount,
    projectSettings,
    overrideStyles,
}) => {
    const [headers, setHeaders] = React.useState<object>({})
    const [data, setData] = React.useState<Record<string, TableContent>[]>([])
    const classes = useStyles({
        ...overrideStyles,
    })

    const isImperialVisible = React.useMemo(
        () =>
            projectSettings?.unitOfMeasure?.toLowerCase() ===
            UnitsOfMeasure.IMPERIAL.split(' ')[0].toLowerCase(),
        [projectSettings]
    )
    const isMetricVisible = React.useMemo(
        () =>
            projectSettings?.unitOfMeasure?.toLowerCase() ===
            UnitsOfMeasure.METRIC.toLowerCase(),
        [projectSettings]
    )
    React.useEffect(() => {
        const columnCount = element.totalColumns
        const column = element?.elements?.reduce(
            (acc: AccumulatorObj, curr: TableContent): AccumulatorObj => {
                const objkeys = Object.keys(acc.headerObj)

                if (
                    !!objkeys &&
                    !!objkeys.length &&
                    acc.headerObj[objkeys.length].length === columnCount
                ) {
                    const currKey = acc.headerObj[objkeys.length][
                        curr.columnNumber - 1
                    ]?.content?.split('.')[0] // key can't contain period in it
                    acc.tempObj[`${currKey}-${curr.columnNumber - 1}`] = curr
                    if (curr.columnNumberEnd === columnCount) {
                        acc.value.push(acc.tempObj)
                        acc.tempObj = {}
                    }
                    return acc
                }

                if (!!acc.headerObj && !!acc.headerObj[curr.rowNumber]) {
                    acc.headerObj[curr.rowNumber].push(curr)
                } else {
                    acc.headerObj[curr.rowNumber] = [curr]
                }

                return acc
            },
            { headerObj: {}, value: [], tempObj: {} }
        )
        if (column) {
            setHeaders(column?.headerObj)
            setData(column?.value)
        }
    }, [element, projectSettings])

    const getHeaderCell = React.useCallback(
        (header) => {
            const width = Math.round(header.cellPoints * 1.33)
            const colSpan =
                header.columnNumberEnd + 1 - header.columnNumberStart
            return (
                <th
                    data-testid="tableHeader"
                    key={header.id}
                    colSpan={colSpan}
                    style={{
                        position: 'relative',
                        textAlign: header.alignment as 'center',
                        width,
                        ...getBorderStyle(header.cellFormat),
                    }}
                    className={classes.tableheader}
                >
                    {renderCellValue(header)}
                </th>
            )
        },
        [projectSettings]
    )

    const columnHelper = createColumnHelper<Record<string, TableContent>>()

    const generateNestedStructure = React.useCallback(
        (data: Array<TableContentWithChildren[]>) => {
            const arr = [...data]
            for (let i = data.length - 2; i >= 0; i -= 1) {
                for (let j = 0; j < data[i].length; j++) {
                    const childRow: TableContent[] = arr[i + 1]
                    const currentCell = arr[i][j]
                    const children = childRow.filter(
                        (c: TableContent) =>
                            c.columnNumberStart >=
                                currentCell.columnNumberStart &&
                            c.columnNumberEnd <= currentCell.columnNumberEnd
                    ) as TableContentWithChildren[]
                    arr[i][j].children = children
                }
            }
            return arr[0]
        },
        []
    )

    const generateHeader = React.useCallback(
        (data: TableContentWithChildren[]): any => {
            if (!data) {
                return
            }
            return data?.map((d: TableContentWithChildren, index: number) => {
                if (!d.children) {
                    return columnHelper.accessor(
                        `${d.content?.split('.')[0]}-${d.columnNumber - 1}`,
                        // this is an error thrown by the package when implementing dynamic headers
                        // this is a work around to make it work
                        // @ts-ignore
                        {
                            id: `${d.content}-${d.columnNumber}`,
                            header: () => getHeaderCell(d),
                            size: Math.round(d.cellPoints * 1.33), //converting point to px
                        }
                    )
                }
                return columnHelper.group({
                    id: `${d.content}-${d.rowNumber}`,
                    header: () => getHeaderCell(d),
                    columns: generateHeader(d.children),
                })
            })
        },
        []
    )

    const columns = React.useMemo(() => {
        const headerKeys = Object.keys(headers)
        if (headerKeys.length > 0) {
            if (headerKeys.length > 1) {
                const input = Object.values(headers)
                const tableHeaderObject = generateNestedStructure(input)

                return generateHeader(tableHeaderObject)
            } else {
                return (headers[
                    `1` as keyof typeof headers
                ] as TableContent[])?.map((header: TableContent, i: number) => {
                    const currKey = header?.content?.split('.')[0] // key can't contain period in it
                    const accessorKey = `${currKey}-${i}`
                    // this is an error thrown by the package when implementing dynamic headers
                    // this is a work around to make it work
                    // @ts-ignore
                    return columnHelper.accessor(accessorKey, {
                        header: () => getHeaderCell(header),
                        cell: (info) => info.getValue(),
                        size: Math.round(header.cellPoints * 1.33), //converting point to px
                    })
                })
            }
        }
    }, [headers, projectSettings])

    const renderCellValue = React.useCallback(
        (value) => {
            const { textContentElements } = value
            return textContentElements?.map(
                (textContentElement: TableCellTextContent) => {
                    const {
                        type,
                        imperialUnits,
                        metricUnits,
                        text,
                    } = textContentElement
                    switch (type) {
                        case 'UnitsOfMeasure':
                            switch (true) {
                                case isImperialVisible:
                                    return (
                                        <span className={classes.IP}>
                                            {imperialUnits}
                                        </span>
                                    )
                                case isMetricVisible:
                                    return (
                                        <span className={classes.SI}>
                                            {metricUnits}
                                        </span>
                                    )
                                default:
                                    return (
                                        <span>
                                            <span className={classes.IP}>
                                                {imperialUnits}
                                            </span>{' '}
                                            <span className={classes.SI}>
                                                ({metricUnits})
                                            </span>
                                        </span>
                                    )
                            }
                        default:
                            return text
                    }
                }
            )
        },
        [isImperialVisible, isMetricVisible, projectSettings]
    )

    const getBorderStyle = React.useCallback(
        (cellFormat) => {
            if (cellFormat) {
                const {
                    bottomBorder,
                    leftBorder,
                    rightBorder,
                    topBorder,
                } = cellFormat
                const style = {
                    SINGLE: 'solid',
                    THICK: 'solid',
                    DOUBLE: 'double',
                    NONE: 'none',
                }

                const sectionPreviewColor = !overrideStyles?.overwriteFontStyles
                    ? overrideStyles?.masterFormatOutlineStyles?.[
                          `paragraphLevel${
                              (parentCount ?? 0) + 1
                          }` as 'paragraphLevel1'
                      ]?.fontColor
                    : overrideStyles?.globalStyles?.font?.fontColor

                const getColorValue = (color: TableCellFormatBorderColor) =>
                    sectionPreviewColor ??
                    `rgb(${color.r}, ${color.g}, ${color.b})`

                const getBorderValue = (border: TableCellFormatBorder) =>
                    `${border?.lineWidth}px ${
                        style[border?.lineStyle as keyof typeof style]
                    } ${getColorValue(border?.color)}`

                return {
                    borderBottom: getBorderValue(bottomBorder),
                    borderLeft: getBorderValue(leftBorder),
                    borderRight: getBorderValue(rightBorder),
                    borderTop: getBorderValue(topBorder),
                }
            }
        },
        [overrideStyles]
    )

    const table = useReactTable({
        data,
        columns: columns ?? [],
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        getCoreRowModel: getCoreRowModel(),
        debugTable: true,
        debugHeaders: true,
        debugColumns: true,
        meta: {
            updateData: (
                rowIndex: number,
                columnId: string,
                value: TableContent
            ) => {
                setData((old: Record<string, TableContent>[]) =>
                    old?.map(
                        (row: Record<string, TableContent>, index: number) => {
                            if (index === rowIndex) {
                                return {
                                    ...old[rowIndex],
                                    [columnId]: value,
                                }
                            }
                            return row
                        }
                    )
                )
            },
        },
    })

    return (
        <div className={classes.row}>
            {hierarchicalLevel && hierarchicalLevel != 1 ? (
                <div className={clsx(classes.cell)}>
                    {indent(hierarchicalLevel, classes)}
                    {hierarchicalLevel && hierarchicalLevel >= 2 ? (
                        <span className={classes.PRspace}></span>
                    ) : (
                        ''
                    )}
                </div>
            ) : (
                ''
            )}
            <div className={classes.cell} data-testid="tableEditor">
                <table className={classes.mainTable}>
                    <thead>
                        {table?.getHeaderGroups()?.map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup?.headers?.map((header) => {
                                    return (
                                        <>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef
                                                          .header,
                                                      header.getContext()
                                                  )}
                                        </>
                                    )
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table?.getRowModel()?.rows?.map((row) => {
                            return (
                                <tr key={row.id}>
                                    {row?.getVisibleCells()?.map((cell, i) => {
                                        let colSpan = 1
                                        let j = i
                                        while (true) {
                                            const isCellNotEmpty =
                                                j ===
                                                    row.getVisibleCells()
                                                        .length -
                                                        1 ||
                                                (row.getVisibleCells()[j + 1] &&
                                                    row
                                                        .getVisibleCells()
                                                        [j + 1].getContext()
                                                        .getValue())
                                            if (isCellNotEmpty) {
                                                break
                                            }
                                            colSpan++
                                            j++
                                        }
                                        const cellValueObj: TableContent = cell
                                            ?.getContext()
                                            ?.getValue() as TableContent
                                        if (!cellValueObj) {
                                            return
                                        }
                                        return (
                                            <td
                                                data-testid="tableCell"
                                                key={cellValueObj.id}
                                                style={{
                                                    width: cell.column.getSize(),
                                                    textAlign: cellValueObj.alignment as 'center',
                                                    ...getBorderStyle(
                                                        cellValueObj.cellFormat
                                                    ),
                                                }}
                                                className={classes.tableCell}
                                                colSpan={colSpan}
                                            >
                                                {renderCellValue(cellValueObj)}
                                            </td>
                                        )
                                    })}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
                <div className="h-4" />
            </div>
        </div>
    )
}
